import { useMemo, useState } from 'react';
import { searchEmoji } from '@td/shared';

/**
 * Choosing an emoji, in the app.
 *
 * What was here before was a text box labelled "Paste an emoji", which works
 * only for somebody who already knows how to open their operating system's
 * emoji panel — on Windows a keyboard shortcut most people have never been
 * told. So the emoji are on screen, grouped, with a search over them.
 *
 * The typing box is kept as well as the grid rather than instead of it. Emoji
 * are added to Unicode every year and this list is a snapshot; anybody who
 * wants one that is not here can still paste it, and it will work.
 */
export function EmojiPicker({
  value,
  onChange,
  label = 'Emoji',
}: {
  value: string;
  onChange: (emoji: string) => void;
  label?: string;
}) {
  const [search, setSearch] = useState('');
  const groups = useMemo(() => searchEmoji(search), [search]);

  const found = groups.reduce((n, g) => n + g.emoji.length, 0);

  return (
    <div className="rounded-lg border border-sand bg-white p-2">
      <div className="mb-2 flex items-center gap-2">
        {/* What is chosen, big enough to actually see. */}
        <span
          aria-label={value ? `${label}: ${value}` : `No ${label.toLowerCase()} chosen`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-linen text-xl"
        >
          {value || '·'}
        </span>

        <input
          className="field flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search — apple, star, bus…"
          aria-label={`Search ${label.toLowerCase()}`}
        />

        {value && (
          <button type="button" className="btn-soft shrink-0" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>

      <div className="max-h-56 overflow-y-auto">
        {found === 0 ? (
          <p className="px-1 py-3 text-sm text-stone">
            Nothing matched &ldquo;{search}&rdquo;. You can still paste one below.
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.name} className="mb-2 last:mb-0">
              <p className="sticky top-0 bg-white px-1 py-1 text-[11px] uppercase tracking-wide text-stone">
                {g.name}
              </p>

              <div className="flex flex-wrap gap-0.5">
                {g.emoji.map((e) => (
                  <button
                    key={e.char + e.keywords}
                    type="button"
                    aria-label={e.keywords || e.char}
                    aria-pressed={value === e.char}
                    title={e.keywords}
                    onClick={() => onChange(e.char)}
                    className={[
                      'flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-colors',
                      value === e.char ? 'bg-terracotta/20 ring-2 ring-terracotta' : 'hover:bg-linen',
                    ].join(' ')}
                  >
                    {e.char}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/*
        Still typeable. The grid is a snapshot of Unicode and Unicode keeps
        growing; anything pasted here works whether or not it is in the list.
      */}
      <input
        className="field mt-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste any emoji"
        maxLength={16}
        aria-label={label}
      />
    </div>
  );
}
